import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Banknote, Plus, Clock, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_UI = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  approved: { label: "Approuvée", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  rejected: { label: "Rejetée", color: "bg-red-100 text-red-800", icon: XCircle },
};
const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} GNF`;

export default function ExpensesPage() {
  const [rows, setRows] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [cats, setCats] = useState([]);
  const [createDlg, setCreateDlg] = useState(false);
  const [form, setForm] = useState({ label: "", amount: "", category: "Autre", beneficiary: "", justification: "" });
  const [decideDlg, setDecideDlg] = useState(null);
  const [decision, setDecision] = useState({ approved: true, note: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/expenses");
      setRows(data.rows); setCanApprove(data.can_approve);
    } catch (e) { toast.error(e.response?.data?.detail || "Accès refusé"); }
    api.get("/expenses/categories").then(r => setCats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Demande envoyée pour validation au Fondateur");
      setCreateDlg(false);
      setForm({ label: "", amount: "", category: "Autre", beneficiary: "", justification: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const decide = async () => {
    setSaving(true);
    try {
      await api.post(`/expenses/${decideDlg.id}/decide`, decision);
      toast.success(decision.approved ? "Sortie d'argent approuvée" : "Demande rejetée");
      setDecideDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const pending = rows.filter(r => r.status === "pending");

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Banknote className="h-3 w-3" /><span>SORTIES D&apos;ARGENT</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Sorties d&apos;argent</h1>
          <p className="text-slate-500 mt-1">Toute sortie d&apos;argent doit être approuvée avant exécution. Chaque décision est tracée au journal d&apos;audit.</p>
        </div>
        <Button onClick={() => setCreateDlg(true)} className="bg-[#1E3A8A] text-white gap-1" data-testid="add-expense-btn"><Plus className="h-4 w-4" />Nouvelle demande</Button>
      </div>

      {canApprove && pending.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-800 font-semibold" data-testid="pending-banner">
          {pending.length} demande{pending.length > 1 ? "s" : ""} en attente de votre validation — total {fmt(pending.reduce((s, r) => s + r.amount, 0))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Libellé</th>
              <th className="px-4 py-3 text-right">Montant</th>
              <th className="px-4 py-3 text-left">Catégorie</th>
              <th className="px-4 py-3 text-left">Bénéficiaire</th>
              <th className="px-4 py-3 text-left">Demandé par</th>
              <th className="px-4 py-3 text-center">Statut</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const st = STATUS_UI[r.status] || STATUS_UI.pending;
              const Ico = st.icon;
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`exp-row-${r.id}`}>
                  <td className="px-4 py-2 font-medium">{r.label}<div className="text-xs text-slate-400 max-w-xs truncate">{r.justification}</div></td>
                  <td className="px-4 py-2 text-right font-bold tabular-nums">{fmt(r.amount)}</td>
                  <td className="px-4 py-2 text-xs">{r.category}</td>
                  <td className="px-4 py-2 text-xs">{r.beneficiary || "—"}</td>
                  <td className="px-4 py-2 text-xs">{r.requested_by_name}<div className="text-slate-400">{new Date(r.requested_at).toLocaleDateString("fr-FR")}</div></td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${st.color}`}><Ico className="h-3 w-3" />{st.label}</span>
                    {r.decided_by_name && <div className="text-[10px] text-slate-400 mt-0.5">par {r.decided_by_name}</div>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canApprove && r.status === "pending" && (
                      <Button size="sm" onClick={() => { setDecideDlg(r); setDecision({ approved: true, note: "" }); }} className="bg-[#166534] text-white" data-testid={`exp-decide-${r.id}`}>Décider</Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Aucune demande de sortie d&apos;argent.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={createDlg} onOpenChange={setCreateDlg}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Nouvelle demande de sortie d&apos;argent</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Libellé *</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Ex : Réparation photocopieuse" data-testid="exp-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Montant (GNF) *</Label>
                <Input type="number" min="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="exp-amount" />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Bénéficiaire</Label>
              <Input value={form.beneficiary} onChange={e => setForm({ ...form, beneficiary: e.target.value })} placeholder="Ex : Fournisseur XYZ" data-testid="exp-beneficiary" />
            </div>
            <div>
              <Label>Justification *</Label>
              <Textarea rows={3} value={form.justification} onChange={e => setForm({ ...form, justification: e.target.value })} data-testid="exp-justification" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDlg(false)}>Annuler</Button>
            <Button onClick={create} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="exp-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer pour validation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decide dialog */}
      <Dialog open={!!decideDlg} onOpenChange={(o) => !o && setDecideDlg(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Décision — {decideDlg?.label}</DialogTitle></DialogHeader>
          {decideDlg && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <div className="font-bold text-lg">{fmt(decideDlg.amount)}</div>
                <div className="text-xs mt-1"><strong>Motif :</strong> {decideDlg.justification}</div>
                <div className="text-xs text-slate-400 mt-1">Demandé par {decideDlg.requested_by_name}</div>
              </div>
              <div className="flex gap-2">
                <Button variant={decision.approved ? "default" : "outline"} className={decision.approved ? "bg-[#166534] text-white flex-1" : "flex-1"} onClick={() => setDecision({ ...decision, approved: true })} data-testid="exp-approve">Approuver</Button>
                <Button variant={!decision.approved ? "default" : "outline"} className={!decision.approved ? "bg-red-600 text-white flex-1" : "flex-1"} onClick={() => setDecision({ ...decision, approved: false })} data-testid="exp-reject">Rejeter</Button>
              </div>
              <div>
                <Label>Note (optionnel)</Label>
                <Textarea rows={2} value={decision.note} onChange={e => setDecision({ ...decision, note: e.target.value })} data-testid="exp-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideDlg(null)}>Annuler</Button>
            <Button onClick={decide} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="exp-decide-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Valider la décision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
